import type { ProductPresetSchema } from '~offerFlow/schemas/ProductPreset';
import * as v from 'valibot';
import { ProductPresetSchemaActivity, ProductPresetSchemaP2P } from '~offerFlow/schemas/ProductPreset';

/**
 * Parse and validate a product preset from a URL query parameter (?pp=...).
 * Seeds runtime/instance state
 * Resolves the origin URN to a Place via API.
 * Returns the parsed preset, or undefined if missing or not applicable.
 * --------------------------------------------------------------------
 */
export default function useProductPreset() {
    const { t: $t } = useNuxtApp().$i18n;
    const { isFeatureEnabled } = useFeatureFlags();
    const { public: { productPresetToolToken } } = useRuntimeConfig();
    const { resolvePlaceNames } = usePlaces();

    const schemas: Record<ProductPresetType, ProductPresetSchema> = {
        P2P: ProductPresetSchemaP2P,
        Activity: ProductPresetSchemaActivity,
    };

    /**
     * Parse, validate, and return product config presets from an encoded string.
     * Resolves place URNs to names via API.
     * Returns the parsed presets, or undefined if the input is missing or invalid.
     */
    async function parseProductPreset(productKey: ProductKey, encoded: string | undefined): Promise<ProductPreset | undefined> {
        const type = productConfigs.find(config => config.key === productKey)?.type as ProductPresetType;

        const schema = schemas[type];
        if (!schema || !encoded) {
            return undefined;
        }

        try {
            const jsonString = isFeatureEnabled('product-presets.encode-url') ? decodeString(encoded, productPresetToolToken) : encoded;
            const parsed = v.parse(schema, JSON.parse(jsonString));
            const preset: ProductPreset = { type, pp: encoded };

            // For P2P presets, resolve place URNs to Place objects with names
            if (preset.type === 'P2P') {
                const {
                    dateFrom,
                    routeVariantUp,
                    routeVariantDown,
                    origin,
                    startingPrice,
                    tripUpId,
                    tripDownId,
                } = parsed as v.InferOutput<typeof ProductPresetSchemaP2P>;

                if (dateFrom) {
                    preset.dateFrom = dateFrom;
                }
                if (routeVariantUp) {
                    preset.routeVariantUp = routeVariantUp;
                }
                if (routeVariantDown) {
                    preset.routeVariantDown = routeVariantDown;
                }
                if (tripUpId) {
                    preset.tripUpId = tripUpId;
                }
                if (tripDownId) {
                    preset.tripDownId = tripDownId;
                }
                if (origin) {
                    preset.origin = (await resolvePlaceNames([origin]))[0];
                }
                if (startingPrice) {
                    preset.startingPrice = startingPrice as Price;
                }
            }
            else if (type === 'Activity') {
                const {
                    dateFrom,
                    dateUntil,
                    startingPrice,
                } = parsed as v.InferOutput<typeof ProductPresetSchemaActivity>;

                if (dateFrom) {
                    preset.dateFrom = dateFrom;
                }

                if (dateUntil) {
                    preset.dateUntil = dateUntil;
                }
                if (startingPrice) {
                    preset.startingPrice = startingPrice as Price;
                }
            }

            return preset;
        }
        catch (error) {
            throw createError({
                message: $t('offerFlow.productConfigPresets.validation.invalid'),
                fatal: true,
                cause: error,
            });
        }
    }

    return {
        parseProductPreset,
    };
}
